import { useMemo } from "react"
import { composeJoinedScheduleMap } from "./JoinedOperation"
import { Operation } from "@/hooks/useListActiveOperations"
import { usePlacementDetailsByOperationId } from "@/hooks/usePlacementDetailsByOperationId"
import { operationListToSchedule } from "@/model/schedule"
import { useCheckinOnDateByOperationIds } from "@/hooks/useCheckinOnDateByOperationIds"
import { usePhotoTourByOperationIds } from "@/hooks/usePhotoTourByOperationId"
import { useHasErrandsByOperationIds } from "@/hooks/useHasErrandsByOperationIds"
import { useListingOccupancyByOperationIds } from "@/hooks/useListingOccupancyByOperationIds"

export const useJoinedSchedule = (token: string, operations: Operation[]) => {
    const operationIds = useMemo(
        () => operations.map(op => op.operationId, []),
        [operations]
    )

    const { data: photoTourByOperationIdMap, isLoading: isLoadingPhotoTours } =
        usePhotoTourByOperationIds(
            token,
            operations
                .filter(({ photoTourId }) => photoTourId)
                .map(({ operationId }) => operationId)
        )

    const { data: placementDetails, isLoading: isLoadingPlacements } =
        usePlacementDetailsByOperationId(token, operationIds)

    const { data: hasCheckinMap, isLoading: isLoadingCheckinOnDate } =
        useCheckinOnDateByOperationIds(token, operationIds)

    const { data: hasErrandsMap, isLoading: isLoadingHasErrandsMap } =
        useHasErrandsByOperationIds(token, operationIds)

    // 在室状況は後から埋まればよいので、一覧の表示をブロックしない
    const { data: occupancyMap } = useListingOccupancyByOperationIds(
        token,
        operationIds
    )

    const joinedSchedules = useMemo(() => {
        const scheduleMap = operationListToSchedule(operations)
        return composeJoinedScheduleMap(scheduleMap, {
            photoTourByOperationIdMap: photoTourByOperationIdMap ?? {},
            placementDetailMap: placementDetails ?? {},
            hasCheckinMap: hasCheckinMap ?? {},
            hasErrandsMap: hasErrandsMap ?? {},
            occupancyMap: occupancyMap ?? {}
        })
    }, [
        operations,
        photoTourByOperationIdMap,
        placementDetails,
        hasCheckinMap,
        hasErrandsMap,
        occupancyMap
    ])

    return {
        isLoading:
            isLoadingPhotoTours ||
            isLoadingPlacements ||
            isLoadingCheckinOnDate ||
            isLoadingHasErrandsMap,
        joinedSchedules
    }
}
